/**
 * <analytics-drill> — the panel every drill mounts into: the trail, the title,
 * the body, and the line saying what the answer below was derived from.
 *
 * ONE FRAME. What every drill owes the operator is identical at every depth —
 * where they are, how to get back, and what the answer below came from — so
 * that is one component rather than a shape each body re-implements and drifts
 * from.
 *
 * THE TRAIL IS THE WAY BACK, AND IT IS ALSO THE LINK. Each crumb's `href` is
 * the real, shareable URL of the chain truncated to that depth — the page the
 * operator is on with the drill parameter replaced — so the trail is copyable
 * at every level; the click handler cancels the navigation and pops the
 * overlay stack instead. The operator gets an in-page pop, and a right-click
 * gets them a link to paste into a message: the same control, no second
 * affordance.
 *
 * EVERY PRESS INSIDE THE PANEL IS THE PANEL'S. A figure knows what it opens
 * and nothing about where it sits; this panel is the one piece of a drill that
 * knows its own depth, so the `drill` event every body bubbles is caught once
 * here and opened from that depth. The chain is a stack — opening from level N
 * cuts it to N first — and a body that answered a press itself would have to
 * know a level it was never told.
 *
 * THE PANEL IS CLAMPED TO THE VIEWPORT, NOT TO ITS MOUNT. An anchored overlay
 * sizes its surface to its content, so a mounted panel's own `max-w-full`
 * resolves against a box as wide as the panel itself and clamps nothing — at
 * 390 px a `w-lg` panel runs off the right edge with the figures on the far
 * side of it, which is the one column this surface exists to show. The panel
 * therefore states its own viewport bound.
 */
import "@mhmo91/schmancy/breadcrumb";
import { overflowWithin } from "@mhmo91/schmancy/directives";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { css, html } from "lit";
import { property } from "lit/decorators.js";
import { choose } from "lit/directives/choose.js";
import { repeat } from "lit/directives/repeat.js";
import { when } from "lit/directives/when.js";
import { LocalizeMixin } from "../../i18n/localize.ts";
import type { DrillNode } from "./drill.state.ts";
import {
	chainHref,
	crumbLabel,
	drillAnswers,
	drillChain,
	drillOpen$,
	drillPop$,
	phraseText,
} from "./drill.state.ts";
import "./boundary.view.ts";
import "./parts.view.ts";
import "./provenance.view.ts";
import "./records.view.ts";

@schmancyElement("analytics-drill")
export class AnalyticsDrill extends LocalizeMixin(SchmancyElement) {
	static styles = [
		css`
			:host {
				display: block;
			}
		`,
	];

	/** Which level of the chain this panel is. 1-based, so it doubles as the
	 *  count of crumbs the trail prints. */
	@property({ type: Number }) depth = 1;

	override render() {
		const chain = drillChain.value.slice(0, this.depth);
		const node: DrillNode | undefined = chain[this.depth - 1];
		if (!node) return html``;
		const answer = drillAnswers.get()[this.depth - 1];
		if (!answer) return html``;
		return html`
			<!-- The panel states its own two measures on its own root, never by
			     resizing the overlay around it: an anchored surface sizes to its
			     content, so a max-width in percent here would resolve against a box
			     as wide as the panel itself and clamp nothing. -->
			<div
				${overflowWithin()}
				class="flex flex-col gap-4 p-5 w-lg max-w-[calc(100vw-2rem)] max-h-[calc(100dvh-4rem)]"
				@drill=${(event: CustomEvent<DrillNode>) =>
					drillOpen$.next({ node: event.detail, from: this.depth })}
			>
				<div class="flex items-start justify-between gap-3">
					<div class="flex flex-col gap-1 min-w-0">
						<schmancy-breadcrumb separator="›" class="text-surface-onVariant">
							${repeat(
								chain,
								(_crumb, index) => `${index}`,
								(crumb, index) => html`
									<schmancy-breadcrumb-item
										href=${chainHref(chain.slice(0, index + 1))}
										?current=${index === chain.length - 1}
										@click=${(event: MouseEvent) => {
											event.preventDefault();
											drillPop$.next(index + 1);
										}}
									>
										<schmancy-typography type="label" token="sm">
											${phraseText(crumbLabel(crumb), this)}
										</schmancy-typography>
									</schmancy-breadcrumb-item>
								`,
							)}
						</schmancy-breadcrumb>
						<schmancy-typography type="title" token="md">
							${phraseText(answer.title, this)}
						</schmancy-typography>
					</div>
					<schmancy-icon-button
						icon="close"
						size="sm"
						aria-label=${this.t("admin.analytics.drill.closeAll")}
						@click=${() => drillPop$.next(0)}
					></schmancy-icon-button>
				</div>

				${choose(answer.body, [
					[
						"parts",
						() =>
							html`<analytics-drill-parts
								.answer=${answer}
							></analytics-drill-parts>`,
					],
					[
						"provenance",
						() =>
							html`<analytics-drill-provenance
								.answer=${answer}
							></analytics-drill-provenance>`,
					],
					[
						"records",
						() =>
							html`<analytics-drill-records
								.answer=${answer}
							></analytics-drill-records>`,
					],
					[
						"boundary",
						() =>
							html`<analytics-drill-boundary
								.answer=${answer}
							></analytics-drill-boundary>`,
					],
				])}

				<!-- THE ATTRIBUTION LINE IS THE PROVENANCE HANDLE. Every panel closes
				     with one line saying what its answer was derived from, and that
				     line carries the same dotted rule the figures do: it opens the
				     full account. Provenance therefore needs no menu, no info glyph
				     and no second gesture — a number is one press from its parts and
				     two from its arithmetic, from anywhere in the chain. The account
				     itself closes on a statement instead, having nothing left to
				     offer to explain. -->
				<div class="border-t border-outline-variant pt-3">
					${when(
						answer.body === "provenance",
						() => html`
							<schmancy-typography
								type="label"
								token="sm"
								class="text-surface-onVariant"
							>
								${this.t("admin.analytics.drill.attributionProvenance")}
							</schmancy-typography>
						`,
						() => html`
							<button
								type="button"
								class="appearance-none bg-transparent border-0 border-b-2 border-dotted border-outline hover:border-primary-default focus-visible:border-primary-default cursor-pointer p-0 pb-0.5 text-left text-surface-onVariant transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary-default"
								@click=${() =>
									drillOpen$.next({
										node: {
											kind: "provenance",
											subject: node.subject,
											scope: node.scope,
										},
										from: this.depth,
									})}
							>
								<schmancy-typography type="label" token="sm">
									${this.t(
										answer.body === "boundary"
											? "admin.analytics.drill.attributionBoundary"
											: answer.body === "records"
												? "admin.analytics.drill.attributionRecords"
												: "admin.analytics.drill.attributionParts",
									)}
								</schmancy-typography>
							</button>
						`,
					)}
				</div>
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"analytics-drill": AnalyticsDrill;
	}
}
